/**
 * guiaWorkbook.js
 * Professional Excel builder for GuiaRemision and GuiaRemisionTransportista modules.
 *
 * Usage:
 *   import { exportGuiasWorkbook, GUIA_CONFIG } from '../utils/excel/guiaWorkbook';
 *   await exportGuiasWorkbook({ guias, searchText, usuario, config: GUIA_CONFIG.REMITENTE });
 */

import { saveAs } from 'file-saver';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const GUIA_CONFIG = {
  REMITENTE: {
    titulo: 'REPORTE PROFESIONAL - GUÍAS DE REMISIÓN REMITENTE',
    sheetResumen: 'Resumen Guías',
    sheetDatos: 'Datos Guías',
    tableName: 'tblGuiasRemision',
    fileName: 'GuiasRemision'
  },
  TRANSPORTISTA: {
    titulo: 'REPORTE PROFESIONAL - GUÍAS DE REMISIÓN TRANSPORTISTA',
    sheetResumen: 'Resumen Guías',
    sheetDatos: 'Datos Guías',
    tableName: 'tblGuiasTransportista',
    fileName: 'GuiasTransportista'
  }
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function normalizeText(value) {
  return String(value ?? '').trim();
}

function toDate(value) {
  if (!value) return null;
  const text = normalizeText(value);
  const m = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateText(value) {
  const d = toDate(value);
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const MOTIVO_DESC = {
  '01': 'Venta', '02': 'Compra', '03': 'Traslado entre establecimientos',
  '04': 'Traslado entre almacenes', '05': 'Consignación', '06': 'Devolución',
  '07': 'Recojo de bienes', '08': 'Importación', '09': 'Exportación',
  '13': 'Traslado emisor itinerante', '17': 'Traslado zona primaria', '18': 'Traslado de zonas',
  '19': 'Traslado por emisor transportista'
};

function resolveMotivo(guia) {
  return guia.motivo_traslado_desc || MOTIVO_DESC[guia.motivo_traslado] || guia.motivo_traslado || '';
}

function buildRows(guias) {
  return guias.map((g, index) => ({
    item: index + 1,
    id: g.id,
    tipo: g.tipo_guia === '09' ? 'Remitente' : 'Transportista',
    documento: `${normalizeText(g.serie)}-${String(Number(g.numero || 0)).padStart(8, '0')}`,
    serie: normalizeText(g.serie),
    numero: Number(g.numero || 0),
    fechaEmision: toDate(g.fecha_emision),
    fechaEmisionText: formatDateText(g.fecha_emision),
    motivo: resolveMotivo(g),
    destinatario: normalizeText(g.destinatario_razon_social),
    destinatarioDoc: normalizeText(g.destinatario_numero_doc),
    puntoPartida: [g.punto_partida_ubigeo, g.punto_partida_direccion].filter(Boolean).join(' · '),
    puntoLlegada: [g.punto_llegada_ubigeo, g.punto_llegada_direccion].filter(Boolean).join(' · '),
    chofer: [g.chofer_nombres, g.chofer_apellidos].filter(Boolean).join(' ') || normalizeText(g.chofer_licencia),
    vehiculoPlaca: normalizeText(g.vehiculo_placa || g.placa_vehiculo),
    estadoCpe: normalizeText(g.estado_cpe) || 'PENDIENTE',
    observaciones: normalizeText(g.observaciones)
  }));
}

// ── Styles ────────────────────────────────────────────────────────────────────

function setThinBorder(cell, color = 'FFD0D7DE') {
  cell.border = {
    top: { style: 'thin', color: { argb: color } },
    left: { style: 'thin', color: { argb: color } },
    bottom: { style: 'thin', color: { argb: color } },
    right: { style: 'thin', color: { argb: color } }
  };
}

function styleMergedBlock(ws, range, fill, fontColor = 'FFFFFFFF', fontSize = 11) {
  ws.mergeCells(range);
  const cell = ws.getCell(range.split(':')[0]);
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  cell.font = { bold: true, color: { argb: fontColor }, size: fontSize };
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  setThinBorder(cell, fill);
  return cell;
}

function addKpi(ws, labelRange, valueRange, label, value, fill) {
  const lc = styleMergedBlock(ws, labelRange, fill, 'FFFFFFFF', 10);
  lc.value = label;
  const vc = styleMergedBlock(ws, valueRange, 'FFF7FAFC', 'FF123247', 16);
  vc.value = value;
  vc.font = { bold: true, size: 16, color: { argb: 'FF123247' } };
  setThinBorder(vc, 'FFD7E3EC');
}

function colorizeEstadoCell(cell, value) {
  const state = normalizeText(value).toUpperCase();
  const palette = {
    ACEPTADO: { fill: 'FFE8F5E9', font: 'FF1B5E20' },
    'ENVIADO A SUNAT': { fill: 'FFFFF4E5', font: 'FF9A6700' },
    ENVIADO: { fill: 'FFFFF4E5', font: 'FF9A6700' },
    RECHAZADO: { fill: 'FFFDECEC', font: 'FFB42318' },
    ANULADA: { fill: 'FFF3F4F6', font: 'FF344054' },
    ANULADO: { fill: 'FFF3F4F6', font: 'FF344054' },
    PENDIENTE: { fill: 'FFEFF8FF', font: 'FF175CD3' },
    PROCESANDO: { fill: 'FFEFF8FF', font: 'FF175CD3' }
  }[state];
  if (!palette) return;
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: palette.fill } };
  cell.font = { bold: true, color: { argb: palette.font } };
}

function styleHeaderRow(row) {
  row.height = 24;
  row.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF123247' } };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    setThinBorder(cell, 'FF123247');
  });
}

function styleBodyRow(row, rowIndex, stateColIndexes = []) {
  row.eachCell((cell) => {
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: rowIndex % 2 === 0 ? 'FFF8FAFC' : 'FFFFFFFF' }
    };
    setThinBorder(cell);
  });
  stateColIndexes.forEach((ci) => colorizeEstadoCell(row.getCell(ci), row.getCell(ci).value));
}

// ── Sheet builders ────────────────────────────────────────────────────────────

function createResumenSheet(workbook, rows, config, searchText, usuario) {
  const ws = workbook.addWorksheet(config.sheetResumen, {
    views: [{ state: 'frozen', ySplit: 8 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });
  ws.properties.defaultRowHeight = 21;
  ws.columns = [
    { width: 7 }, { width: 18 }, { width: 18 }, { width: 16 },
    { width: 22 }, { width: 18 }, { width: 16 }, { width: 14 }
  ];

  // Row 1 title
  ws.mergeCells('A1:H1');
  ws.getCell('A1').value = config.titulo;
  ws.getCell('A1').font = { bold: true, size: 18, color: { argb: 'FFFFFFFF' } };
  ws.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF123247' } };
  ws.getRow(1).height = 28;

  ws.mergeCells('A2:H2');
  ws.getCell('A2').value = `Generado el ${new Date().toLocaleString('es-PE')}${usuario ? ` | Usuario: ${usuario}` : ''}`;
  ws.getCell('A2').font = { italic: true, size: 10, color: { argb: 'FF475467' } };
  ws.getCell('A2').alignment = { horizontal: 'center' };
  ws.getRow(2).height = 20;

  ws.mergeCells('A3:H3');
  ws.getCell('A3').value = `Filtro aplicado: ${searchText || 'Todos los registros visibles'}`;
  ws.getCell('A3').font = { size: 10, color: { argb: 'FF344054' } };
  ws.getCell('A3').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FA' } };
  ws.getCell('A3').alignment = { horizontal: 'left' };

  // KPIs
  const totalDocs = rows.length;
  const aceptadas = rows.filter((r) => r.estadoCpe.toUpperCase() === 'ACEPTADO').length;
  const pendientes = rows.filter((r) => r.estadoCpe.toUpperCase() === 'PENDIENTE').length;
  const rechazadas = rows.filter((r) => r.estadoCpe.toUpperCase() === 'RECHAZADO').length;

  addKpi(ws, 'A5:B5', 'A6:B7', 'Total Guías', totalDocs, 'FF175CD3');
  addKpi(ws, 'D5:E5', 'D6:E7', 'Aceptadas SUNAT', aceptadas, 'FF027A48');
  addKpi(ws, 'G5:H5', 'G6:H7', 'Pendientes', pendientes, 'FFB54708');
  addKpi(ws, 'A8:B8', 'A9:B10', 'Rechazadas', rechazadas, 'FFDC2626');

  // Table header
  const HEADER_ROW = 12;
  ws.getRow(HEADER_ROW).values = ['Documento', 'Emisión', 'Motivo', 'Destinatario', 'Doc. dest.', 'Vehículo', 'Estado CPE', 'Observaciones'];
  styleHeaderRow(ws.getRow(HEADER_ROW));

  rows.forEach((row, index) => {
    const excelRow = ws.getRow(HEADER_ROW + 1 + index);
    excelRow.values = [row.documento, row.fechaEmision || row.fechaEmisionText, row.motivo, row.destinatario, row.destinatarioDoc, row.vehiculoPlaca, row.estadoCpe, row.observaciones];
    excelRow.getCell(2).numFmt = 'dd/mm/yyyy';
    excelRow.height = 20;
    styleBodyRow(excelRow, index, [7]);
  });

  const lastDataRow = Math.max(HEADER_ROW, HEADER_ROW + rows.length);
  ws.autoFilter = { from: { row: HEADER_ROW, column: 1 }, to: { row: HEADER_ROW, column: 8 } };

  const totRow = ws.getRow(lastDataRow + 1);
  totRow.getCell(1).value = `TOTAL: ${totalDocs} guías`;
  totRow.height = 22;
  totRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FF123247' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF2F8' } };
    setThinBorder(cell, 'FFB9C8D6');
  });
}

function createDatosSheet(workbook, rows, config) {
  const ws = workbook.addWorksheet(config.sheetDatos, { views: [{ state: 'frozen', ySplit: 1 }] });

  ws.addTable({
    name: config.tableName,
    ref: 'A1',
    headerRow: true,
    totalsRow: true,
    style: { theme: 'TableStyleMedium2', showRowStripes: true },
    columns: [
      { name: 'Item', totalsRowLabel: 'Totales' }, { name: 'ID' }, { name: 'Tipo' },
      { name: 'Documento' }, { name: 'Serie' }, { name: 'Numero' },
      { name: 'FechaEmision' }, { name: 'Motivo' }, { name: 'Destinatario' },
      { name: 'DocDestinatario' }, { name: 'PuntoPartida' }, { name: 'PuntoLlegada' },
      { name: 'Chofer' }, { name: 'VehiculoPlaca' }, { name: 'EstadoCPE' }, { name: 'Observaciones' }
    ],
    rows: rows.map((r) => [r.item, r.id, r.tipo, r.documento, r.serie, r.numero, r.fechaEmisionText, r.motivo, r.destinatario, r.destinatarioDoc, r.puntoPartida, r.puntoLlegada, r.chofer, r.vehiculoPlaca, r.estadoCpe, r.observaciones])
  });

  ws.columns = [
    { width: 8 }, { width: 8 }, { width: 14 }, { width: 18 }, { width: 10 }, { width: 10 },
    { width: 14 }, { width: 24 }, { width: 30 }, { width: 18 }, { width: 28 }, { width: 28 },
    { width: 22 }, { width: 16 }, { width: 18 }, { width: 26 }
  ];
  ws.getRow(1).font = { bold: true };
  ws.getCell(`A${rows.length + 4}`).value = 'Base lista para filtros avanzados y tabla dinámica en Excel.';
  ws.getCell(`A${rows.length + 4}`).font = { italic: true, color: { argb: 'FF667085' } };
}

// ── Public export ──────────────────────────────────────────────────────────────

/**
 * @param {Object} options
 * @param {Array}  options.guias       - Filtered guias records
 * @param {string} options.searchText  - Current search text
 * @param {string} options.usuario     - Logged-in username
 * @param {Object} options.config      - GUIA_CONFIG.REMITENTE | GUIA_CONFIG.TRANSPORTISTA
 */
export async function exportGuiasWorkbook({ guias, searchText, usuario, config }) {
  const ExcelJSModule = await import('exceljs');
  const ExcelJS = ExcelJSModule.default || ExcelJSModule;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'InsightSoft';
  workbook.created = new Date();

  const rows = buildRows(guias);
  createResumenSheet(workbook, rows, config, searchText, usuario);
  createDatosSheet(workbook, rows, config);

  const buffer = await workbook.xlsx.writeBuffer();
  const timestamp = new Date().toISOString().slice(0, 10);
  saveAs(new Blob([buffer], { type: EXCEL_MIME }), `${config.fileName}-${timestamp}.xlsx`);
}
